import {createSlice, current, PayloadAction} from "@reduxjs/toolkit";
import {IChat} from "./chat.interface";
import {ISms} from "./chat.interface";

import {
    autocompleteChats,
    changeSendingNumber, changeSlot,
    deleteSMSExpired,
    getChat,
    getChats,
    loadMoreChats,
    sendChatSms, unreadChat,
    updateChat
} from "./chat.request";
import {toast} from "react-toastify";

export type ChatState = {
    chat: IChat | null;
    chats: IChat[];
    autocomplete: IChat[] | null;
    limit: number;
    offset: number;
    isLoading: boolean;
    isLoadChat: boolean;
    isLoadingActiveNumber: boolean;
    pendingStatusUpdates: {
        [messageId: string]: 'sent' | 'delivered' | 'failed';
    };
    enteredSms: {chatId: string, text: string}[];
};

const initialState: ChatState = {
    chat: null,
    chats: [],
    autocomplete: null,
    limit: 32,
    offset: 0,
    isLoading: false,
    isLoadChat: false,
    isLoadingActiveNumber: false,
    pendingStatusUpdates: {},
    enteredSms: []
};

const chatSlice = createSlice({
    name: "chat",
    initialState,
    reducers: {
        saveSms: (state, action: PayloadAction<{ chatId: string; text: string }>) => {
            const { chatId, text } = action.payload;

            const existingSms = state.enteredSms.find((sms) => sms.chatId === chatId);

            if (existingSms) {
                existingSms.text = text;
            } else {
                state.enteredSms.push({ chatId, text });
            }
        },
        clearSmsInChat: (state, action: PayloadAction<{ chatId: string }>) => {
            const { chatId } = action.payload;

            state.enteredSms = state.enteredSms.filter((sms) => sms.chatId !== chatId);
        },
        clearChat: (state) => {
            state.chat = null
        },
        clearChats: (state) => {
            state.chats = []
            state.offset = 0
        },
        clearAutocompleteChats: (state) => {
            state.autocomplete = null
        },
        updateMessageStatus: (
            state,
            action: PayloadAction<{
                chatId: string;
                messageId: string;
                status: 'sent' | 'delivered' | 'failed';
            }>
        ) => {
            const { chatId, messageId, status } = action.payload;

            let messageFound = false;

            if (state.chat && state.chat.id === chatId) {
                if (state.chat.messages) {
                    state.chat = {
                        ...state.chat,
                        messages: state.chat.messages.map(msg => {
                            if (msg.id === messageId) {
                                messageFound = true;
                                return { ...msg, status };
                            }
                            return msg;
                        }),
                    };
                }
            }

            state.chats = state.chats.map(chat => {
                if (chat.id === chatId && chat.messages) {
                    const updatedMessages = chat.messages.map(msg => {
                        if (msg.id === messageId) {
                            messageFound = true;
                            return { ...msg, status };
                        }
                        return msg;
                    });
                    return { ...chat, messages: updatedMessages };
                }
                return chat;
            });

            if (!messageFound) {
                state.pendingStatusUpdates[messageId] = status;
            }


        },
        onNewSms: (state, action) => {
            const data: ISms = action.payload.sms
            if (!state.chat) return
            const current_chat = current(state.chat)
            if (data.chat_id === current_chat?.id) {
                const messages = current_chat.messages || []
                if (state.chat) {
                    state.chat.messages = [...messages, data]
                }
            }
        },
        onNewChat: (state, action) => {
            const new_chat: IChat = action.payload
            let current_chats = [...current(state.chats)]
            let tagged_chats = current_chats.filter(chat => chat.is_top)
            let untagged_chats = current_chats.filter(chat => !chat.is_top)
            if(new_chat.is_top) {
                const chat_index = tagged_chats.findIndex(it => it.id === new_chat.id)
                if (chat_index === -1) {
                    state.chats = [new_chat, ...tagged_chats, ...untagged_chats]
                } else {
                    tagged_chats.splice(chat_index, 1)
                    state.chats = [new_chat, ...tagged_chats, ...untagged_chats]
                }
            } else {
                const chat_index = untagged_chats.findIndex(it => it.id === new_chat.id)
                if (chat_index === -1) {
                    state.chats = [...tagged_chats, new_chat, ...untagged_chats]
                } else {
                    untagged_chats.splice(chat_index, 1)
                    state.chats = [...tagged_chats, new_chat, ...untagged_chats]
                }
            }
        },
        onOpenChat: (state, action) => {
            const chat_id: string = action.payload
            let current_chats = [...current(state.chats)]

            state.chats = current_chats.map(chat => chat.id === chat_id ? { ...chat, have_unseen_sms: false } : chat)
        },
        onUpdateUnreadChat: (state, action:PayloadAction<{ id: string }>  ) => {
            //have_unseen_sms
            state.chats.map(chat => {
                if(chat.id === action.payload.id) {
                    chat.have_unseen_sms = true
                }
                return chat
            })
        }
    },
    extraReducers: (builder) => {
        builder
            // get
            .addMatcher(
                (action) => action.type === getChat.pending.type,
                (state) => {
                    state.isLoadChat = true;
                }
            )
            .addMatcher(
                (action) => action.type === getChat.fulfilled.type,
                (state, action: PayloadAction<{chat: IChat}>) => {
                    state.chat = action.payload.chat
                    state.isLoadChat = false;
                }
            )
            .addMatcher(
                (action) => action.type === getChat.rejected.type,
                (state, action: any) => {
                    state.isLoadChat = false;
                }
            )
            // get all
            .addMatcher(
                (action) => action.type === getChats.pending.type,
                (state) => {
                    state.isLoading = true;
                }
            )
            .addMatcher(
                (action) => action.type === getChats.fulfilled.type,
                (state, action: PayloadAction<{chats: IChat[]}>) => {
                    // state.offset = 0
                    state.chats = [...action.payload.chats]

                    // console.log('offset', current(state).offset)
                    // console.log('limit', current(state).limit)
                    // console.log('new offset', current(state).offset + current(state).limit + 1)


                    state.offset = current(state).offset + current(state).limit + 1;


                    state.isLoading = false;
                }
            )
            .addMatcher(
                (action) => action.type === getChats.rejected.type,
                (state, action: any) => {
                    state.isLoading = false;
                }
            )
            // load more
            .addMatcher(
                (action) => action.type === loadMoreChats.pending.type,
                (state) => {
                    state.isLoading = true;
                }
            )
            .addMatcher(
                (action) => action.type === loadMoreChats.fulfilled.type,
                (state, action: PayloadAction<{chats: IChat[]}>) => {
                    state.chats = [...current(state.chats), ...action.payload.chats]
                    state.offset = current(state).offset + current(state).limit + 1;
                    state.isLoading = false;
                }
            )
            .addMatcher(
                (action) => action.type === loadMoreChats.rejected.type,
                (state, action: any) => {
                    state.isLoading = false;
                }
            )
            // update
            .addMatcher(
                (action) => action.type === updateChat.pending.type,
                (state) => {
                    state.isLoading = true;
                }
            )
            .addMatcher(
                (action) => action.type === updateChat.fulfilled.type,
                (state, action: PayloadAction<{chat: IChat}>) => {
                    state.chats = current(state.chats).map(chat => chat.id === action.payload.chat.id ? action.payload.chat : chat)
                    state.isLoading = false;
                }
            )
            .addMatcher(
                (action) => action.type === updateChat.rejected.type,
                (state, action: any) => {
                    state.isLoading = false;
                }
            )
            // delete sms expired
            .addMatcher(
                (action) => action.type === deleteSMSExpired.pending.type,
                (state) => {
                    state.isLoading = true;
                }
            )
            .addMatcher(
                (action) => action.type === deleteSMSExpired.fulfilled.type,
                (state, action: any) => {
                    state.isLoading = false;
                }
            )
            .addMatcher(
                (action) => action.type === deleteSMSExpired.rejected.type,
                (state, action: any) => {
                    state.isLoading = false;
                }
            )
            // change sending number
            .addMatcher(
                (action) => action.type === changeSendingNumber.pending.type,
                (state) => {
                    state.isLoading = true;
                }
            )
            .addMatcher(
                (action) => action.type === changeSendingNumber.fulfilled.type,
                (state, action: any) => {
                    state.isLoading = false;
                }
            )
            .addMatcher(
                (action) => action.type === changeSendingNumber.rejected.type,
                (state, action: any) => {
                    state.isLoading = false;
                }
            )
            // send
            .addMatcher(
                (action) => action.type === sendChatSms.pending.type,
                (state) => {
                    // state.isLoading = true;
                }
            )
            .addMatcher(
                (action) => action.type === sendChatSms.fulfilled.type,
                (state, action: PayloadAction<{message: ISms, id: string, expired: boolean}>) => {

                    if(state.pendingStatusUpdates[action.payload.message.id]) {
                        action.payload.message.status = state.pendingStatusUpdates[action.payload.message.id];
                    }

                    let chat = current(state.chat)?.id === action.payload.id ? current(state.chat) : null
                    if (chat && state.chat) {
                        chat = { ...current(state.chat), messages: [...current(state.chat)?.messages || [], action.payload.message ] }
                        state.chat = chat
                    }
                    state.isLoading = false;

                    if(!chat) return
                    let current_chats = [...current(state.chats)]
                    let tagged_chats = current_chats.filter(chat => chat.is_top)
                    let untagged_chats = current_chats.filter(chat => !chat.is_top)
                    if(chat.is_top) {
                        const chat_index = tagged_chats.findIndex(it => it.id === chat?.id)
                        if (chat_index === -1) {
                            state.chats = [chat, ...tagged_chats, ...untagged_chats]
                        } else {
                            tagged_chats.splice(chat_index, 1)
                            state.chats = [chat, ...tagged_chats, ...untagged_chats]
                        }
                    } else {
                        const chat_index = untagged_chats.findIndex(it => it.id === chat?.id)
                        if (chat_index === -1) {
                            state.chats = [...tagged_chats, chat, ...untagged_chats]
                        } else {
                            untagged_chats.splice(chat_index, 1)
                            state.chats = [...tagged_chats, chat, ...untagged_chats]
                        }
                    }
                }
            )
            .addMatcher(
                (action) => action.type === sendChatSms.rejected.type,
                (state, action: any) => {
                    // state.isLoading = false;
                }
            )
            // autocomplete
            .addMatcher(
                (action) => action.type === autocompleteChats.pending.type,
                (state) => {
                    // state.isLoading = true;
                }
            )
            .addMatcher(
                (action) => action.type === autocompleteChats.fulfilled.type,
                (state, action: PayloadAction<{chats: IChat[] | null}>) => {
                    state.autocomplete = action.payload.chats
                }
            )
            .addMatcher(
                (action) => action.type === autocompleteChats.rejected.type,
                (state, action: any) => {
                    // state.isLoading = false;
                }
            )
            // unread
            .addMatcher(
                (action) => action.type === unreadChat.pending.type,
                (state) => {
                    // state.isLoading = true;
                }
            )
            .addMatcher(
                (action) => action.type === unreadChat.fulfilled.type,
                (state, action) => {
                }
            )
            .addMatcher(
                (action) => action.type === unreadChat.rejected.type,
                (state, action) => {
                    // state.isLoading = false;
                }
            )

            .addMatcher(
                (action) => action.type === changeSlot.pending.type,
                (state) => {
                    state.isLoadingActiveNumber = true;
                }
            )
            .addMatcher(
                (action) => action.type === changeSlot.fulfilled.type,
                (state, action) => {
                    state.isLoadingActiveNumber = false;
                    toast("The number has been successfully activated for 10 minutes", { type: 'success' })
                }
            )
            .addMatcher(
                (action) => action.type === changeSlot.rejected.type,
                (state, action) => {
                    state.isLoadingActiveNumber = false;
                    toast("The chat either has no sender number assigned or the sender number no longer exists", { type: 'error' })
                }
            )


    },
});

export const { clearChat, clearChats, clearAutocompleteChats, onNewSms, onNewChat, onOpenChat, updateMessageStatus, saveSms, clearSmsInChat, onUpdateUnreadChat } = chatSlice.actions;

export default chatSlice.reducer;
